import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Input, Tag, Typography } from "antd";
import { searchDocumentExtractedText } from "../../service/analysis";

/**
 * Search-the-document bar backed by the AI extraction, not the PDF's
 * embedded text layer.
 *
 * Why: on scanned/handwritten VA records the PDF's own text layer is
 * decades-old scanner OCR garbage, so the viewer's built-in search
 * misses text the AI extraction reads cleanly (the 2026-08-12 trigger:
 * a real in-service "SHINSPLINTS" ER diagnosis was unfindable). This
 * bar queries `GET /documents/<id>/extracted-search/` and lists
 * page-anchored hits; clicking one hands `{ page, snippet }` to the
 * parent, which jumps the Apryse viewer.
 *
 * Stateless with respect to the viewer — the parent owns the Apryse
 * instance and the jump. This keeps the bar reusable on any surface
 * that can supply `documentId` + an `onJumpToPage` callback.
 */
const ExtractedTextSearchBar = ({ documentId, onJumpToPage }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = no search yet
  const [searching, setSearching] = useState(false);
  const requestSeq = useRef(0);

  // Clear stale results when the modal is reused for another document.
  useEffect(() => {
    setQuery("");
    setResults(null);
    setSearching(false);
    requestSeq.current += 1;
  }, [documentId]);

  const runSearch = useCallback(
    async (value) => {
      const trimmed = (value || "").trim();
      if (!documentId || trimmed.length < 2) {
        setResults(null);
        return;
      }
      const seq = ++requestSeq.current;
      setSearching(true);
      try {
        const response = await searchDocumentExtractedText(
          documentId,
          trimmed
        );
        if (seq !== requestSeq.current) return; // superseded
        setResults(response?.data?.results || []);
      } catch {
        if (seq !== requestSeq.current) return;
        setResults([]);
      } finally {
        if (seq === requestSeq.current) setSearching(false);
      }
    },
    [documentId]
  );

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
      data-testid="extracted-text-search"
    >
      <Input.Search
        allowClear
        size="small"
        placeholder="Search document text (AI-extracted — finds text even in poor scans)"
        value={query}
        loading={searching}
        onChange={(event) => {
          setQuery(event.target.value);
          if (!event.target.value.trim()) setResults(null);
        }}
        onSearch={runSearch}
        data-testid="extracted-text-search-input"
      />
      {Array.isArray(results) && (
        <div
          style={{ maxHeight: 180, overflowY: "auto" }}
          data-testid="extracted-text-search-results"
        >
          {results.length === 0 && !searching && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              No matches in the extracted text.
            </Typography.Text>
          )}
          {results.map((hit, idx) => (
            <div
              key={`${hit.page ?? "doc"}-${idx}`}
              role="button"
              tabIndex={0}
              onClick={() => onJumpToPage(hit.page, hit.snippet)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onJumpToPage(hit.page, hit.snippet);
                }
              }}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "baseline",
                padding: "4px 6px",
                borderRadius: 4,
                cursor: hit.page != null ? "pointer" : "default",
              }}
              data-testid="extracted-text-search-hit"
            >
              <Tag style={{ flexShrink: 0 }}>
                {hit.page != null ? `p. ${hit.page}` : "no page"}
              </Tag>
              <Typography.Text
                style={{ fontSize: 12 }}
                ellipsis={{ tooltip: hit.snippet }}
              >
                {hit.snippet}
                {hit.match_count > 1 ? ` (${hit.match_count}×)` : ""}
              </Typography.Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

ExtractedTextSearchBar.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onJumpToPage: PropTypes.func.isRequired,
};

ExtractedTextSearchBar.defaultProps = {
  documentId: null,
};

export default ExtractedTextSearchBar;
